import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface QuickReply {
  id: string;
  account_id: string;
  name: string;
  content: string;
  shortcut: string | null;
  category: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useQuickReplies() {
  const { user } = useAuth();
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReplies = useCallback(async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('account_templates')
        .select('*')
        .eq('account_id', user.account.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setReplies(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.account?.id]);

  const createReply = async (reply: Omit<QuickReply, 'id' | 'account_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data, error: createError } = await supabase
        .from('account_templates')
        .insert({
          account_id: user.account.id,
          ...reply
        })
        .select()
        .single();

      if (createError) throw createError;

      setReplies(prev => [data, ...prev]);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const updateReply = async (id: string, updates: Partial<QuickReply>) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_templates')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;

      setReplies(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteReply = async (id: string) => {
    if (!supabase) return false;

    try {
      const { error: deleteError } = await supabase
        .from('account_templates')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setReplies(prev => prev.filter(r => r.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchReplies();
  }, [fetchReplies]);

  return {
    replies,
    loading,
    error,
    fetchReplies,
    createReply,
    updateReply,
    deleteReply
  };
}
